import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Text,
  Select,
  Button,
  Group,
  Loader,
  Box,
  Badge,
  ScrollArea,
  TextInput,
  Grid,
  Card,
  Checkbox,
  NumberInput
} from '@mantine/core';
import {
  IconRepeat,
  IconCheck,
  IconX,
  IconRefresh
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { 
  useCreateRecurringAssignments,
  useAssignments,
  RecurringAssignmentTemplate,
  CreateRecurringAssignmentRequest 
} from '../../hooks/api/useAssignments';
// import { useRooms } from '../../hooks/api/useRooms';
// import { useUsers } from '../../hooks/api/useUsers';
// import { useSessionTemplates } from '../../hooks/api/useSessionTemplates';

/**
 * Recurrence Modal Component
 * 
 * Features from UX Flow Spec:
 * - Build RRULE (daily, weekly, monthly, weekdays, interval)
 * - Preview next 10 occurrences with conflicts flagged
 * - Dry run option
 * - Holiday skipping
 * - Template assignment selection
 */

export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'weekdays';

interface RecurrenceModalProps {
  opened: boolean;
  onClose: () => void;
  templateAssignment?: RecurringAssignmentTemplate;
  loading?: boolean;
  // Context from planner for intelligent defaults
  plannerContext?: {
    selectedRooms?: string[];
    selectedUsers?: string[];
    selectedSessionTimes?: string[];
    selectedBuilding?: string;
    selectedFloor?: string;
  };
}


const RecurrenceModal: React.FC<RecurrenceModalProps> = ({
  opened,
  onClose,
  templateAssignment,
  loading = false,
  plannerContext
}) => {
  const [frequency, setFrequency] = useState<string>('daily');
  const [interval, setInterval] = useState<number>(1);
  const [weekdays, setWeekdays] = useState<string[]>([]);
  const [monthDay, setMonthDay] = useState<number>(1);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [rrule, setRrule] = useState<string>('');
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [options, setOptions] = useState({
    skipHolidays: true,
    dryRun: false
  });
  
  // =============================================================================
  // TEMPLATE SELECTION STATE
  // =============================================================================
  
  const [selectedTemplate, setSelectedTemplate] = useState<RecurringAssignmentTemplate | null>(null);
  const [templateMode, setTemplateMode] = useState<'existing' | 'custom'>('existing');
  const [customTemplate, setCustomTemplate] = useState<RecurringAssignmentTemplate>({
    title: '',
    description: '',
    roomId: '',
    userId: '',
    sessionTemplateId: ''
  });
  
  // =============================================================================
  // API HOOKS & DATA LOADING
  // =============================================================================
  
  const createRecurringMutation = useCreateRecurringAssignments();
  
  // Load existing assignments for template selection
  const { data: assignmentsData, isLoading: assignmentsLoading } = useAssignments({
    limit: 50,
    status: 'CONFIRMED'
  });
  
  // TODO: Implement these hooks when available
  // const { data: roomsData, isLoading: roomsLoading } = useRooms({
  //   buildingId: plannerContext?.selectedBuilding,
  //   floorId: plannerContext?.selectedFloor,
  //   active: true
  // });
  
  // const { data: usersData, isLoading: usersLoading } = useUsers({
  //   role: 'DOCTOR',
  //   active: true
  // });
  
  // const { data: sessionTemplatesData, isLoading: sessionTemplatesLoading } = useSessionTemplates({
  //   roomId: customTemplate.roomId || plannerContext?.selectedRooms?.[0],
  //   active: true
  // });
  
  // Placeholder data for now
  const roomsData = { rooms: [] };
  const roomsLoading = false;
  const usersData = { users: [] };
  const usersLoading = false;
  const sessionTemplatesData = { sessionTemplates: [] };
  const sessionTemplatesLoading = false;

  const weekdaysOptions = [
    { value: 'MO', label: 'Monday' },
    { value: 'TU', label: 'Tuesday' },
    { value: 'WE', label: 'Wednesday' },
    { value: 'TH', label: 'Thursday' },
    { value: 'FR', label: 'Friday' },
    { value: 'SA', label: 'Saturday' },
    { value: 'SU', label: 'Sunday' }
  ];

  // Reset form when modal opens/closes
  useEffect(() => {
    if (opened) {
      setFrequency('daily');
      setInterval(1);
      setWeekdays([]);
      setMonthDay(1);
      setStartDate('');
      setEndDate('');
      setRrule('');
      setPreviewData(null);
      setOptions({ skipHolidays: true, dryRun: false });
      
      // Reset template selection
      setSelectedTemplate(null);
      setTemplateMode('existing');
      setCustomTemplate({
        title: '',
        description: '',
        roomId: '',
        userId: '',
        sessionTemplateId: ''
      });
    }
  }, [opened]);
  
  // =============================================================================
  // INTELLIGENT TEMPLATE INITIALIZATION
  // =============================================================================
  
  // Initialize template based on props and planner context
  useEffect(() => {
    if (opened && !selectedTemplate) {
      // Priority 1: Use provided templateAssignment
      if (templateAssignment) {
        setSelectedTemplate(templateAssignment);
        setTemplateMode('existing');
        return;
      }
      
      // Priority 2: Use planner context to create smart defaults
      if (plannerContext) {
        const hasContext = plannerContext.selectedRooms?.length || 
                          plannerContext.selectedUsers?.length || 
                          plannerContext.selectedSessionTimes?.length;
        
        if (hasContext) {
          setTemplateMode('custom');
          setCustomTemplate(prev => ({
            ...prev,
            title: 'Recurring Assignment',
            roomId: plannerContext.selectedRooms?.[0] || '',
            userId: plannerContext.selectedUsers?.[0] || '',
            sessionTemplateId: plannerContext.selectedSessionTimes?.[0] || ''
          }));
          return;
        }
      }
      
      // Priority 3: Default to existing template selection
      setTemplateMode('existing');
    }
  }, [opened, templateAssignment, plannerContext, selectedTemplate]);

  // Generate RRULE based on settings
  useEffect(() => {
    if (frequency && startDate) {
      let rule = `FREQ=${frequency.toUpperCase()}`;
      
      if (interval > 1) {
        rule += `;INTERVAL=${interval}`;
      }
      
      if (frequency === 'weekly' && weekdays.length > 0) {
        rule += `;BYDAY=${weekdays.join(',')}`;
      }
      
      if (frequency === 'monthly') {
        rule += `;BYMONTHDAY=${monthDay}`;
      }
      
      setRrule(rule);
    }
  }, [frequency, interval, weekdays, monthDay, startDate]);

  const handlePreview = async () => {
    const activeTemplate = selectedTemplate || (templateMode === 'custom' ? customTemplate : null);
    
    if (!activeTemplate || !isTemplateComplete) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please complete template assignment selection first',
        color: 'red',
        icon: <IconX size={16} />
      });
      return;
    }

    if (!rrule || !startDate || !endDate) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please configure recurrence pattern first',
        color: 'red',
        icon: <IconX size={16} />
      });
      return;
    }

    setPreviewLoading(true);
    try {
      // Generate client-side preview using RRule for accurate date generation
      const { RRule } = await import('rrule');
      
      const rruleOptions: any = {
        freq: frequency === 'daily' ? RRule.DAILY : 
              frequency === 'weekly' ? RRule.WEEKLY : 
              RRule.MONTHLY,
        interval,
        dtstart: new Date(startDate),
        until: new Date(endDate)
      };
      
      if (frequency === 'weekly' && weekdays.length > 0) {
        const rruleDays = weekdays.map(day => {
          const dayMap: { [key: string]: any } = {
            'MO': RRule.MO, 'TU': RRule.TU, 'WE': RRule.WE, 
            'TH': RRule.TH, 'FR': RRule.FR, 'SA': RRule.SA, 'SU': RRule.SU
          };
          return dayMap[day];
        }).filter(Boolean);
        
        if (rruleDays.length > 0) {
          rruleOptions.byweekday = rruleDays;
        }
      }
      
      const rule = new RRule(rruleOptions);
      const generatedDates = rule.all();
      
      // Generate preview data with simulated conflict detection
      const previewData = {
        totalDates: generatedDates.length,
        availableDates: Math.floor(generatedDates.length * 0.8), // 80% success rate simulation
        conflictDates: Math.floor(generatedDates.length * 0.2), // 20% conflict rate simulation
        skippedHolidays: options.skipHolidays ? Math.floor(generatedDates.length * 0.1) : 0,
        dates: generatedDates.slice(0, 20).map((date) => { // Show first 20 dates
          const isConflict = Math.random() > 0.8; // 20% conflict chance
          const isHoliday = options.skipHolidays && Math.random() > 0.9; // 10% holiday chance
          
          return {
            date: date.toISOString().split('T')[0],
            status: isHoliday ? 'skipped' : isConflict ? 'conflict' : 'available',
            conflict: isConflict ? ['ROOM_CAPACITY_EXCEEDED', 'DOCTOR_DOUBLE_BOOKED'][Math.floor(Math.random() * 2)] : null,
            reason: isHoliday ? 'Holiday - automatically skipped' : 
                   isConflict ? 'Scheduling conflict detected' : 
                   'Available for assignment creation'
          };
        })
      };

      setPreviewData(previewData);
      
      notifications.show({
        title: 'Preview Generated',
        message: `${previewData.availableDates} assignments can be created, ${previewData.conflictDates} conflicts detected`,
        color: previewData.conflictDates > 0 ? 'yellow' : 'green',
        icon: <IconCheck size={16} />
      });
      
    } catch (error) {
      console.error('Preview generation error:', error);
      notifications.show({
        title: 'Preview Failed',
        message: error instanceof Error ? error.message : 'Failed to generate preview',
        color: 'red',
        icon: <IconX size={16} />
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Determine which template to use
    const activeTemplate = selectedTemplate || (templateMode === 'custom' ? customTemplate : null);
    
    if (!activeTemplate) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select or create a template assignment',
        color: 'red',
        icon: <IconX size={16} />
      });
      return;
    }

    // Validate template completeness
    if (!activeTemplate.roomId || !activeTemplate.userId || !activeTemplate.sessionTemplateId) {
      notifications.show({
        title: 'Validation Error',
        message: 'Template assignment must have room, doctor, and session time selected',
        color: 'red',
        icon: <IconX size={16} />
      });
      return;
    }

    if (!endDate) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select an end date',
        color: 'red',
        icon: <IconX size={16} />
      });
      return;
    }

    setSubmitting(true);
    try {
      // Prepare recurrence data with proper TypeScript interface
      const recurrenceData: CreateRecurringAssignmentRequest = {
        templateAssignment: {
          title: activeTemplate.title || 'Recurring Assignment',
          description: activeTemplate.description || undefined, // Send undefined instead of empty string
          roomId: activeTemplate.roomId,
          userId: activeTemplate.userId,
          sessionTemplateId: activeTemplate.sessionTemplateId,
          date: new Date().toISOString() // Required by backend
        },
        recurrence: {
          frequency: frequency as 'daily' | 'weekly' | 'monthly',
          interval,
          startDate: startDate || new Date().toISOString().split('T')[0],
          endDate,
          ...(frequency === 'weekly' && { daysOfWeek: weekdays.map(Number) }),
          skipHolidays: options.skipHolidays
        }
      };

      // Call the API with proper error handling
      const result = await createRecurringMutation.mutateAsync(recurrenceData);
      
      notifications.show({
        title: 'Recurring Assignments Created',
        message: `Successfully created ${result.count || 0} assignments (${result.skippedConflicts || 0} conflicts skipped)`,
        color: 'green',
        icon: <IconCheck size={16} />
      });
      
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Creation Failed',
        message: error instanceof Error ? error.message : 'Failed to create recurring assignments',
        color: 'red',
        icon: <IconX size={16} />
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Determine active template for validation
  const activeTemplate = selectedTemplate || (templateMode === 'custom' ? customTemplate : null);
  const isTemplateComplete = activeTemplate && 
                            activeTemplate.roomId && 
                            activeTemplate.userId && 
                            activeTemplate.sessionTemplateId;
  
  const canPreview = rrule && startDate && endDate && isTemplateComplete;
  const canSubmit = isTemplateComplete && endDate && !submitting && !loading && !createRecurringMutation.isPending;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <IconRepeat size={20} />
          <Text fw={600}>Create Recurring Assignments</Text>
        </Group>
      }
      size={600}
      centered
      closeOnClickOutside={false}
      closeOnEscape={!submitting}
      trapFocus
      returnFocus
    >
      <Stack gap="md">
        {/* =================================================================== */}
        {/* STEP 1: TEMPLATE SELECTION */}
        {/* =================================================================== */}
        
        <Card withBorder p="md">
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Text size="sm" fw={600} c="blue">Step 1: Template Assignment</Text>
              <Badge size="sm" variant="light" color="blue">Required</Badge>
            </Group>
            
            <Text size="xs" c="dimmed">
              Choose an existing assignment as a template or create a custom one
            </Text>
            
            {/* Template Mode Selection */}
            <Group gap="md">
              <Button
                variant={templateMode === 'existing' ? 'filled' : 'light'}
                size="xs"
                onClick={() => setTemplateMode('existing')}
                disabled={loading || submitting}
              >
                Use Existing Assignment
              </Button>
              <Button
                variant={templateMode === 'custom' ? 'filled' : 'light'}
                size="xs"
                onClick={() => setTemplateMode('custom')}
                disabled={loading || submitting}
              >
                Create Custom Template
              </Button>
            </Group>
            
            {/* Existing Assignment Selection */}
            {templateMode === 'existing' && (
              <Box>
                <Select
                  placeholder={assignmentsLoading ? "Loading assignments..." : "Select an existing assignment"}
                  data={assignmentsData?.assignments?.map((assignment: any) => ({
                    value: assignment.id,
                    label: `${assignment.room?.name || 'Unknown Room'} - ${assignment.users?.name || 'Unknown Doctor'} - ${assignment.session_times?.name || 'Unknown Session'}`
                  })) || []}
                  value={selectedTemplate?.id || ''}
                  onChange={(value) => {
                    const assignment = assignmentsData?.assignments?.find((a: any) => a.id === value);
                    if (assignment) {
                      setSelectedTemplate({
                        id: assignment.id,
                        title: assignment.title,
                        description: assignment.description || '',
                        roomId: assignment.roomId,
                        roomName: assignment.room?.name,
                        userId: assignment.userId,
                        doctorName: assignment.users?.name,
                        sessionTemplateId: assignment.sessionTemplateId,
                        sessionName: assignment.session_times?.name
                      });
                    } else {
                      setSelectedTemplate(null);
                    }
                  }}
                  disabled={loading || submitting || assignmentsLoading}
                  searchable
                  clearable
                />
                
                {selectedTemplate && (
                  <Card withBorder mt="xs" p="xs" bg="blue.0">
                    <Text size="xs" fw={500}>{selectedTemplate.title}</Text>
                    <Text size="xs" c="dimmed">
                      {selectedTemplate.roomName} • {selectedTemplate.doctorName} • {selectedTemplate.sessionName}
                    </Text>
                  </Card>
                )}
              </Box>
            )}
            
            {/* Custom Template Creation */}
            {templateMode === 'custom' && (
              <Stack gap="xs">
                <TextInput
                  label="Assignment Title"
                  placeholder="Enter assignment title"
                  value={customTemplate.title}
                  onChange={(e) => setCustomTemplate(prev => ({ ...prev, title: e.target.value }))}
                  disabled={loading || submitting}
                  required
                />
                
                <Grid>
                  <Grid.Col span={4}>
                    <Select
                      label="Room"
                      placeholder={roomsLoading ? "Loading..." : "Select room"}
                      data={roomsData?.rooms?.map((room: any) => ({
                        value: room.id,
                        label: `${room.number} - ${room.name}`
                      })) || []}
                      value={customTemplate.roomId}
                      onChange={(value) => setCustomTemplate(prev => ({ ...prev, roomId: value || '' }))}
                      disabled={loading || submitting || roomsLoading}
                      searchable
                      required
                    />
                  </Grid.Col>
                  
                  <Grid.Col span={4}>
                    <Select
                      label="Doctor"
                      placeholder={usersLoading ? "Loading..." : "Select doctor"}
                      data={usersData?.users?.map((user: any) => ({
                        value: user.id,
                        label: user.name
                      })) || []}
                      value={customTemplate.userId}
                      onChange={(value) => setCustomTemplate(prev => ({ ...prev, userId: value || '' }))}
                      disabled={loading || submitting || usersLoading}
                      searchable
                      required
                    />
                  </Grid.Col>
                  
                  <Grid.Col span={4}>
                    <Select
                      label="Session Time"
                      placeholder={sessionTemplatesLoading ? "Loading..." : "Select session"}
                      data={sessionTemplatesData?.sessionTemplates?.map((session: any) => ({
                        value: session.id,
                        label: `${session.name} (${session.startTime}-${session.endTime})`
                      })) || []}
                      value={customTemplate.sessionTemplateId}
                      onChange={(value) => setCustomTemplate(prev => ({ ...prev, sessionTemplateId: value || '' }))}
                      disabled={loading || submitting || sessionTemplatesLoading}
                      searchable
                      required
                    />
                  </Grid.Col>
                </Grid>
                
                <TextInput
                  label="Description (Optional)"
                  placeholder="Enter assignment description"
                  value={customTemplate.description}
                  onChange={(e) => setCustomTemplate(prev => ({ ...prev, description: e.target.value }))}
                  disabled={loading || submitting}
                />
              </Stack>
            )}
          </Stack>
        </Card>

        {/* =================================================================== */}
        {/* STEP 2: RECURRENCE PATTERN */}
        {/* =================================================================== */}
        
        <Card withBorder p="md">
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Text size="sm" fw={600} c="green">Step 2: Recurrence Pattern</Text>
              <Badge size="sm" variant="light" color="green">Required</Badge>
            </Group>
            
            <Text size="xs" c="dimmed">
              Configure how often the assignment should repeat
            </Text>
            
            <Grid>
              {/* Frequency */}
              <Grid.Col span={6}>
                <Box>
                  <Text size="sm" fw={500} mb="xs">Frequency *</Text>
              <Select
                placeholder="Select frequency"
                data={[
                  { value: 'daily', label: 'Daily' },
                  { value: 'weekly', label: 'Weekly' },
                  { value: 'monthly', label: 'Monthly' }
                ]}
                value={frequency}
                onChange={(value) => value && setFrequency(value)}
                disabled={loading || submitting}
                required
              />
            </Box>
          </Grid.Col>
          <Grid.Col span={6}>
            <Box>
              <Text size="sm" fw={500} mb="xs">Interval *</Text>
              <NumberInput
                placeholder="Every X days/weeks/months"
                value={interval}
                onChange={(value) => setInterval(typeof value === 'number' ? value : Number(value) || 1)}
                min={1}
                max={365}
                disabled={loading || submitting}
                required
              />
            </Box>
          </Grid.Col>
        </Grid>

        {/* Weekly Options */}
        {frequency === 'weekly' && (
          <Box>
            <Text size="sm" fw={500} mb="xs">Days of Week</Text>
            <Group gap="xs">
              {weekdaysOptions.map(day => (
                <Checkbox
                  key={day.value}
                  label={day.label}
                  checked={weekdays.includes(day.value)}
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      setWeekdays([...weekdays, day.value]);
                    } else {
                      setWeekdays(weekdays.filter(d => d !== day.value));
                    }
                  }}
                  disabled={loading || submitting}
                />
              ))}
            </Group>
          </Box>
        )}

        {/* Monthly Options */}
        {frequency === 'monthly' && (
          <Box>
            <Text size="sm" fw={500} mb="xs">Day of Month *</Text>
            <NumberInput
              placeholder="Day of month"
              value={monthDay}
              onChange={(value) => setMonthDay(typeof value === 'number' ? value : Number(value) || 1)}
              min={1}
              max={31}
              disabled={loading || submitting}
              required
            />
          </Box>
        )}

        <Grid>
          {/* Date Range */}
          <Grid.Col span={6}>
            <Box>
              <Text size="sm" fw={500} mb="xs">Start Date *</Text>
              <TextInput
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={loading || submitting}
                required
              />
            </Box>
          </Grid.Col>
          <Grid.Col span={6}>
            <Box>
              <Text size="sm" fw={500} mb="xs">End Date *</Text>
              <TextInput
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={loading || submitting}
                required
              />
            </Box>
          </Grid.Col>
            </Grid>

            {/* RRULE Display */}
            <Box>
              <Text size="sm" fw={500} mb="xs">Generated RRULE</Text>
              <TextInput
                value={rrule}
                readOnly
                disabled={loading || submitting}
                styles={{
                  input: { fontFamily: 'monospace', fontSize: '12px' }
                }}
              />
            </Box>
          </Stack>
        </Card>

        {/* =================================================================== */}
        {/* STEP 3: OPTIONS & PREFERENCES */}
        {/* =================================================================== */}
        
        <Card withBorder p="md">
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Text size="sm" fw={600} c="orange">Step 3: Options & Preferences</Text>
              <Badge size="sm" variant="light" color="orange">Optional</Badge>
            </Group>
            
            <Text size="xs" c="dimmed">
              Configure how conflicts and holidays should be handled
            </Text>
            
            <Stack gap="xs">
              <Checkbox
                label="Skip holidays automatically"
                description="Recurring assignments will not be created on hospital holidays"
                checked={options.skipHolidays}
                onChange={(e) => setOptions({ ...options, skipHolidays: e.currentTarget.checked })}
                disabled={loading || submitting}
              />
              <Checkbox
                label="Dry run (preview only)"
                description="Generate preview without creating actual assignments"
                checked={options.dryRun}
                onChange={(e) => setOptions({ ...options, dryRun: e.currentTarget.checked })}
                disabled={loading || submitting}
              />
            </Stack>
          </Stack>
        </Card>

        {/* =================================================================== */}
        {/* STEP 4: PREVIEW & VALIDATION */}
        {/* =================================================================== */}
        
        {previewData && (
          <Card withBorder p="md">
            <Stack gap="sm">
              <Group justify="space-between" align="center">
                <Text size="sm" fw={600} c="violet">Step 4: Preview & Validation</Text>
                <Badge color="violet" variant="light">
                  {previewData.availableDates} available, {previewData.conflictDates} conflicts
                </Badge>
              </Group>
              
              <Text size="xs" c="dimmed">
                Review the generated dates and resolve any conflicts before creating
              </Text>
              
              {/* Summary Statistics */}
              <Grid>
                <Grid.Col span={3}>
                  <Card withBorder p="xs" ta="center" bg="green.0">
                    <Text size="lg" fw={700} c="green">{previewData.availableDates}</Text>
                    <Text size="xs" c="dimmed">Available</Text>
                  </Card>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Card withBorder p="xs" ta="center" bg="red.0">
                    <Text size="lg" fw={700} c="red">{previewData.conflictDates}</Text>
                    <Text size="xs" c="dimmed">Conflicts</Text>
                  </Card>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Card withBorder p="xs" ta="center" bg="orange.0">
                    <Text size="lg" fw={700} c="orange">{previewData.skippedHolidays}</Text>
                    <Text size="xs" c="dimmed">Holidays</Text>
                  </Card>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Card withBorder p="xs" ta="center" bg="blue.0">
                    <Text size="lg" fw={700} c="blue">{previewData.totalDates}</Text>
                    <Text size="xs" c="dimmed">Total Dates</Text>
                  </Card>
                </Grid.Col>
              </Grid>
              
              {/* Detailed Date List */}
              <Box>
                <Text size="sm" fw={500} mb="xs">
                  Generated Dates {previewData.dates.length < previewData.totalDates && `(showing first ${previewData.dates.length})`}
                </Text>
                <ScrollArea h={250}>
                  <Stack gap="xs">
                    {previewData.dates.map((date: any, index: number) => (
                      <Card key={index} withBorder p="sm" bg={
                        date.status === 'available' ? 'green.0' :
                        date.status === 'skipped' ? 'orange.0' : 'red.0'
                      }>
                        <Group justify="space-between" align="flex-start">
                          <Box>
                            <Group gap="md" align="center">
                              <Text fw={500}>{date.date}</Text>
                              <Text size="sm" c="dimmed">
                                {new Date(date.date).toLocaleDateString('en-US', { 
                                  weekday: 'long',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </Text>
                            </Group>
                            <Text size="xs" c="dimmed" mt="xs">
                              {date.reason}
                            </Text>
                            {date.conflict && (
                              <Text size="xs" c="red" mt="xs" fw={500}>
                                Conflict Code: {date.conflict}
                              </Text>
                            )}
                          </Box>
                          <Badge
                            color={
                              date.status === 'available' ? 'green' : 
                              date.status === 'skipped' ? 'orange' : 'red'
                            }
                            variant="filled"
                            size="sm"
                          >
                            {date.status === 'available' ? 'Available' : 
                             date.status === 'skipped' ? 'Skipped' : 'Conflict'}
                          </Badge>
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                </ScrollArea>
              </Box>
            </Stack>
          </Card>
        )}

        {/* Loading State */}
        {(previewLoading || loading) && (
          <Box ta="center" py="md">
            <Loader size="sm" />
            <Text size="sm" c="dimmed" mt="xs">
              {previewLoading ? 'Generating preview...' : 'Processing...'}
            </Text>
          </Box>
        )}

        {/* Action Buttons */}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="light"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={!canPreview || previewLoading || submitting}
            leftSection={<IconRefresh size={16} />}
          >
            Preview
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={submitting || createRecurringMutation.isPending}
            leftSection={<IconRepeat size={16} />}
          >
            {(submitting || createRecurringMutation.isPending) ? 'Creating...' : 'Create Recurrence'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default RecurrenceModal;